import { Link as ReactLink } from 'react-router-dom';
import { css, CSSProperties, styled } from 'styled-components';

import { getUrlType, useTabbing } from '../../utils';
import { theme } from '../../styles';
import { zIndex } from '../../styles/zIndex';
import { Chevron, ExternalLink, StyledChevron } from './Icons';

type StyledLinkProps = {
  testId?: string;
  $styleAs: 'link' | 'button';
  variant: 'primary' | 'transparent' | 'outline';
  fontSize: 'small' | 'standard' | 'medium' | 'large';
  $hasIcon: boolean;
  $fullWidth?: boolean;
  disabled?: boolean;
  $urlType?: 'internal' | 'external';
  $isTabbing?: boolean;
  $iconLeft?: boolean;
};

export type LinkProps = {
  /** Set `data-testid` for testing purpose */
  testId?: string;
  /** Link to send the user to when `<Link/>` is clicked.
   * If address is external, will use <a> tag, if internal, react-router's Link component is used. */
  to: string;
  /** Optional function to be triggered when `<Link/>` is clicked */
  onClick?: () => void;
  /** Depending on what you choose, `<Link/>` will change
   * its style to look like a normal `link` or `button`,
   * but it will still function like a link, see example in Storybook
   */
  styleAs?: 'link' | 'button';
  /** Changes the styles/variant of `<Link/>`,
   * choose correct `variant` depending on what Design has decided
   */
  variant?: 'primary' | 'transparent' | 'outline';
  /** Depending on what you choose, the `font-size` will be changed.
   * `medium` = 18px,
   * `standard` = 16px,
   * `small` = 14px (desktop), 16px tablet/mobile
   */
  fontSize?: 'small' | 'standard' | 'medium' | 'large';
  /** If this is set to `true` a Chevron icon will appear at the end of the link */
  hasIcon?: boolean;
  /** Sets `<Link/>` to have 100% width (of it's parent width), mainly used i.e. `<Footer />` */
  fullWidth?: boolean;
  /** Sets `<Link/>` as disabled, mainly used when `styleAs = 'button'`  */
  disabled?: boolean;
  /** React Router prop, only applicable for internal (react-router) links */
  reloadDocument?: boolean;
  /** Default right */
  iconLeft?: boolean;
  /** Styling */
  style?: CSSProperties;
  /** React Router prop */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  state?: any;
  /** Text to display in `<Link/> */
  children: React.ReactNode;
};

const styling = css<StyledLinkProps>`
  color: ${theme.colors.foraBlue};
  text-decoration: none;
  user-select: none;
  appearance: none;
  outline: none;

  // not using display: flex on regular link so that the icon comes on the same line as the text text when text is long and wrapped.

  ${props => props.fontSize === 'small' && theme.typography.textSmall}
  ${props => props.fontSize === 'standard' && theme.typography.textStandard}
  ${props => props.fontSize === 'medium' && theme.typography.textMedium}
  ${props => props.fontSize === 'large' && theme.typography.textLarge}

  ${props =>
    props.variant === 'primary' &&
    css`
      color: ${theme.colors.foraBlue};
      ${props.$isTabbing &&
      `
      &:focus {
        outline: ${theme.colors.black} solid 2px;
        outline-offset: 2px;
      }`}
    `}

  ${props =>
    props.variant === 'transparent' &&
    css`
      color: ${theme.colors.white};
      ${props.$isTabbing &&
      `
      &:focus {
        outline: ${theme.colors.white} solid 2px;
        outline-offset: 2px;
      }`}
    `}

  ${props =>
    props.$styleAs === 'button' &&
    css`
      /* Use similar CSS-attributes from Button.tsx */
      display: inline-flex;
      align-items: center;
      box-sizing: border-box;
      padding: 0.875rem 2.125rem;
      border-radius: 2px;
      justify-content: center;
      ${props.$fullWidth && 'width: 100%'};
      min-height: 3.125rem;

      &:hover,
      &:focus {
        text-decoration: none !important;
      }

      ${props.variant === 'primary' &&
      css`
        color: ${theme.colors.white};
        background-color: ${theme.colors.foraBlue};
        &:hover {
          background-color: ${theme.colors.darkBlue};
        }

        ${props.variant === 'primary' &&
        props.disabled &&
        css`
          color: ${theme.colors.black60} !important;
          background-color: ${theme.colors.black20};
          border-bottom: 0;
        `}
      `};

      ${props.variant === 'transparent' &&
      css`
        color: ${theme.colors.white};
        border: 1px solid ${theme.colors.white};
        background-color: transparent;
      `};

      ${props.variant === 'outline' &&
      css`
        color: ${theme.colors.foraBlue};
        border: 1px solid ${theme.colors.foraBlue};
        background-color: transparent;
        &:hover {
          box-shadow: 0px 0px 0px 1px ${theme.colors.foraBlue};
        }
        &:active {
          box-shadow: 0px 0px 0px 2px ${theme.colors.foraBlue};
        }

        ${props.variant === 'outline' &&
        props.disabled &&
        css`
          color: ${theme.colors.black50} !important;
          border: 1px solid ${theme.colors.black50};
        `}
        ${props.$isTabbing &&
        css`
          &:focus {
            outline: ${theme.colors.foraBlue} solid 2px;
            outline-offset: 2px;
          }
        `}
      `};

      @media screen and (max-width: ${theme.breakpoint.md - 1}px) {
        min-height: 3.75rem;
        ${props.$fullWidth !== false && 'width: 100%'};
      }
    `}

  ${props =>
    props.disabled &&
    css`
      color: ${theme.colors.lightGrey};
      pointer-events: none;
    `}

  &:hover, &:focus {
    text-decoration: underline;

    & > ${StyledChevron} {
      transform: translateX(${props => (props.$iconLeft ? '-5px' : '5px')});
    }
  }
`;

// Use react-router Link for internal routing
const StyledReactRouterLink = styled(ReactLink)<StyledLinkProps>`
  ${styling}
`;

// Use regular anchor tags for external links
const StyledAnchorLink = styled.a<StyledLinkProps>`
  ${styling}
`;

export const BaseLink = ({
  testId,
  to,
  styleAs = 'link',
  fontSize = 'standard',
  variant = 'primary',
  hasIcon = true,
  iconLeft,
  fullWidth,
  disabled,
  onClick,
  reloadDocument,
  style,
  state,
  children,
}: LinkProps) => {
  const urlType = getUrlType(to);
  const isTabbing = useTabbing();

  if (urlType === 'internal') {
    const iconColor =
      styleAs === 'button' && variant === 'primary' ? 'white' : variant === 'transparent' ? 'white' : 'foraBlue';
    return (
      <StyledReactRouterLink
        data-testid={testId}
        to={to}
        onClick={() => onClick?.()}
        fontSize={fontSize}
        variant={variant}
        disabled={disabled}
        $isTabbing={isTabbing}
        $styleAs={styleAs}
        $fullWidth={fullWidth}
        $urlType={urlType}
        $hasIcon={hasIcon}
        reloadDocument={reloadDocument}
        state={state}
        style={style}
        $iconLeft={iconLeft}
      >
        {hasIcon && iconLeft && <Chevron color={iconColor} fontSize={fontSize} iconLeft />}
        {children}
        {hasIcon && !iconLeft && <Chevron color={iconColor} fontSize={fontSize} />}
      </StyledReactRouterLink>
    );
  }
  if (urlType === 'external') {
    const iconColor =
      styleAs === 'button' && variant === 'primary' ? 'white' : variant === 'transparent' ? 'white' : 'foraBlue';
    return (
      <StyledAnchorLink
        data-testid={testId}
        href={to}
        onClick={() => onClick?.()}
        $styleAs={styleAs}
        $fullWidth={fullWidth}
        fontSize={fontSize}
        variant={variant}
        disabled={disabled}
        $isTabbing={isTabbing}
        target="_blank"
        rel="noopener noreferrer"
        style={style}
        $hasIcon={hasIcon}
        $iconLeft={iconLeft}
      >
        {hasIcon && iconLeft && <ExternalLink color={iconColor} fontSize={fontSize} iconLeft />}
        {children}
        {hasIcon && !iconLeft && <ExternalLink color={iconColor} fontSize={fontSize} />}
      </StyledAnchorLink>
    );
  }
  return (
    <StyledAnchorLink
      data-testid={testId}
      fontSize={fontSize}
      variant={variant}
      disabled={disabled}
      $styleAs={styleAs}
      $isTabbing={isTabbing}
      href="#"
      style={style}
      $hasIcon={hasIcon}
    >
      {children}
    </StyledAnchorLink>
  );
};

/** Hidden until focused anchor element to jump to a specific place on a page.
 * Not using react-router because if the anchor is on the same page, the Link element does not lose focus,
 * which beats the purpose skip links when using keyboard navigation */
export const SkipLink = ({ href, skipToContentText }: { href: string; skipToContentText: string }) => (
  <HiddenUntilFocusedAnchorLink
    href={href}
    variant="transparent"
    $styleAs="button"
    $fullWidth={false}
    $hasIcon={true}
    $urlType="internal"
    $isTabbing={true}
    fontSize="standard"
  >
    {skipToContentText}
    <Chevron color="white" fontSize="standard" />
  </HiddenUntilFocusedAnchorLink>
);

/** Hidden until focused https://www.a11y-collective.com/blog/skip-to-main-content/ */
const HiddenUntilFocusedAnchorLink = styled(StyledAnchorLink)`
  position: absolute;
  left: -9999px; //Moves the link far off-screen to the left, making it invisible to sighted users.
  z-index: ${zIndex.modal};
  opacity: 0;

  &:focus {
    position: static;
    background-color: ${theme.colors.foraBlue};
    opacity: 1;
    margin: 20px 40px 0px;
  }
`;
